import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Check } from "lucide-react";
import { COMPANY } from "../../data/whitespaces";
import { Reveal, MaskLine } from "../../lib/motionPrimitives";
import { MagneticButton } from "../MagneticButton";
import EmblemWatermark from "../EmblemWatermark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Data Licensing", "Programmatic", "Persona Activation", "Full Deck Walkthrough"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [interest, setInterest] = useState(INTERESTS[0]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error("Please complete name, email and company.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, interest });
      setDone(true);
      toast.success("Request received. Our team will reach out shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative overflow-hidden border-t border-white/10 bg-black/20 px-6 py-14 backdrop-blur-sm md:px-12 md:py-20"
    >
      <div className="pointer-events-none absolute inset-0 spotlight" />
      <EmblemWatermark className="-right-24 top-1/2 h-[40vw] w-[40vw] max-w-[580px] -translate-y-1/2 rotate-12" />
      <div className="relative mx-auto max-w-[1600px]">
        <div className="mb-8 flex items-center gap-4">
          <span className="font-body text-[11px] uppercase tracking-[0.3em] text-teal">05 — Partnership</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <div className="grid gap-16 md:grid-cols-2 md:gap-24">
          {/* Left copy */}
          <div>
            <h2 className="font-display text-5xl font-medium leading-[0.98] tracking-tight text-white md:text-7xl">
              <MaskLine>Let's build</MaskLine>
              <MaskLine delay={0.08}>the top</MaskLine>
              <MaskLine delay={0.16} className="text-teal">
                of your funnel.
              </MaskLine>
            </h2>
            <Reveal delay={0.2} className="mt-10 max-w-md">
              <p className="font-body text-base leading-relaxed text-white/50">
                Book a private walkthrough of the Whitespaces deck. We'll map your KPIs against 69.1M+
                leads and show the exact path to measurable downstream growth.
              </p>
            </Reveal>
            <Reveal delay={0.25} className="mt-12">
              <div className="font-body text-[11px] uppercase tracking-[0.2em] text-white/30">
                Global offices
              </div>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                {COMPANY.offices.map((o) => (
                  <span key={o} className="font-display text-lg text-white/70">
                    {o}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <Reveal delay={0.15}>
            {done ? (
              <div
                data-testid="lead-success"
                className="flex h-full min-h-[380px] flex-col items-start justify-center rounded-xl border border-teal/30 bg-white/[0.02] p-10 backdrop-blur-xl"
              >
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-teal text-black">
                  <Check size={26} />
                </span>
                <h3 className="mt-6 font-display text-3xl font-medium text-white">Request received.</h3>
                <p className="mt-3 max-w-sm font-body text-white/50">
                  Thank you. A Whitespaces strategist will reach out within one business day.
                </p>
                <button
                  data-testid="lead-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 font-body text-[12px] uppercase tracking-[0.2em] text-teal hover:text-white"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="lead-form" className="space-y-10">
                <div className="grid gap-10 sm:grid-cols-2">
                  <Field label="Name" testId="lead-name" value={form.name} onChange={update("name")} placeholder="Your name" />
                  <Field
                    label="Work Email"
                    testId="lead-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@company.com"
                  />
                </div>
                <Field label="Company" testId="lead-company" value={form.company} onChange={update("company")} placeholder="Company name" />

                <div>
                  <label className="font-body text-[11px] uppercase tracking-[0.2em] text-white/40">
                    I'm interested in
                  </label>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {INTERESTS.map((it) => (
                      <button
                        key={it}
                        type="button"
                        data-testid={`interest-${it.split(" ")[0].toLowerCase()}`}
                        onClick={() => setInterest(it)}
                        className={`rounded-full border px-4 py-2 font-body text-[12px] tracking-wide transition-colors duration-300 ${
                          interest === it
                            ? "border-teal bg-teal text-black"
                            : "border-white/15 text-white/60 hover:border-white/40"
                        }`}
                      >
                        {it}
                      </button>
                    ))}
                  </div>
                </div>

                <Field
                  label="Message (optional)"
                  testId="lead-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your goals"
                />

                <MagneticButton
                  type="submit"
                  testId="lead-submit"
                  className="group flex w-full items-center justify-between rounded-full border border-teal bg-teal px-8 py-5 font-body text-sm uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-transparent hover:text-teal disabled:opacity-50"
                >
                  <span>{loading ? "Sending…" : "Book a Demo"}</span>
                  <ArrowUpRight className="transition-transform duration-300 group-hover:rotate-45" size={20} />
                </MagneticButton>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testId, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="font-body text-[11px] uppercase tracking-[0.2em] text-white/40">{label}</label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="mt-3 w-full rounded-none border-0 border-b border-white/20 bg-transparent pb-3 font-body text-lg text-white outline-none transition-colors duration-300 placeholder:text-white/20 focus:border-teal"
    />
  </div>
);

export default Contact;
